import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Card,  
  Button,  
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,  
  Fab,
  Badge,
} from '@mui/material';
import {
  getOrCreateCurrentOrder,
  addOrderItemAsync,
  removeOrderItemAsync,
  updateOrderItemAsync,  
  prepareMyItemsAsync
} from '../store/slices/orderSlice';
import { setPageLoading, setCartOpen, setMenuScrollState, clearMenuScrollState } from '../store/slices/uiSlice'; // Import setPageLoading
import type { AppDispatch, RootState } from '../store';
import type { MenuItem, Category } from '../store/slices/menuSlice';
import type { CartItem, SelectedOptions } from '../types/menu';
import CartDrawer from '../components/CartDrawer';
import api from '../services/api';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaymentIcon from '@mui/icons-material/Payment';
import { wsService } from '../services/websocket';
import {
  trackPageView,
  trackMenuInteraction,
  trackAddToCart,
  trackRemoveFromCart,
  trackBeginCheckout,
  trackCartEvent,
  trackTableSession,
  trackUserEngagement,
  identifyUser,
  trackOrderEvent,
  trackWithUserContext,
  trackTableMetrics,
  incrementTableVisit,
  getDeviceId,
  getRestaurantId,
  setRestaurantId
} from '../utils/analytics';

// Helper function to compare selectedOptions
const optionsAreEqual = (optionsA?: CartItem['selectedOptions'], optionsB?: CartItem['selectedOptions']): boolean => {
  if (optionsA === optionsB) return true; // Handles undefined === undefined
  if (!optionsA || !optionsB) return false;

  const aEntries = Object.entries(optionsA).sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
  const bEntries = Object.entries(optionsB).sort(([keyA], [keyB]) => keyA.localeCompare(keyB));

  if (aEntries.length !== bEntries.length) return false;

  return aEntries.every(([key, aChoices], index) => {
    const [bKey, bChoices] = bEntries[index];
    if (key !== bKey || aChoices.length !== bChoices.length) return false;

    const sortedAChoices = [...aChoices].sort((c1, c2) => c1.name.localeCompare(c2.name));
    const sortedBChoices = [...bChoices].sort((c1, c2) => c1.name.localeCompare(c2.name));

    return sortedAChoices.every((aChoice, choiceIndex) => {
      const bChoice = sortedBChoices[choiceIndex];
      return aChoice.name === bChoice.name && aChoice.price === bChoice.price;
    });
  });
};

// Helper to generate a consistent key for a group of items
const getGroupKeyForCharacteristics = (menuItemId: string, notes?: string, selectedOptions?: CartItem['selectedOptions']) => {
  const optionsKey = selectedOptions
    ? JSON.stringify(
        Object.entries(selectedOptions)
          .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
          .map(([groupName, choices]) => ({
            groupName,
            choices: choices.slice().sort((a, b) => a.name.localeCompare(b.name)),
          }))
      )
    : '';
  return `${menuItemId}-${notes || ''}-${optionsKey}`;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`menu-tabpanel-${index}`}
      aria-labelledby={`menu-tab-${index}`}
      style={{ 
        width: '100%', 
        minHeight: 0,
        contain: 'layout style', // CSS containment to prevent layout shifts
      }}
      {...other}
    >
      {value === index && (
        <Box sx={{ 
          p: 0, 
          width: '100%', 
          contain: 'layout style', // CSS containment for stable rendering
        }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const PublicMenu = () => {
  const { qrCode } = useParams<{ qrCode: string }>();
  const navigate = useNavigate();
  const location = useLocation(); // Get location object
  const dispatch = useDispatch<AppDispatch>();
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const cartOpen = useSelector((state: RootState) => state.ui.isCartOpen);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [notes, setNotes] = useState('');
  const [table, setTable] = useState<{ id: string } | null>(null);
  const [itemActionLoading, setItemActionLoading] = useState<{ [key: string]: boolean }>({});
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const currentOrder = useSelector((state: RootState) => state.order.currentOrder);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, { name: string; price: number }[]>>({});
  const [quantity, setQuantity] = useState(1);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const userLoading = useSelector((state: RootState) => state.user.loading);
  const isPageLoading = useSelector((state: RootState) => state.ui.isPageLoading);
  const [fabPosition, setFabPosition] = useState({ isAtBottom: false, lastScrollY: 0 });
  const [restaurantId, setRestaurantIdState] = useState<string | undefined>(undefined);
  const [restaurantData, setRestaurantData] = useState<{ id: string; name: string; imageUrl?: string } | null>(null);
  const menuScrollState = useSelector((state: RootState) => state.ui.menuScrollState);

  const readyToPayCount = useMemo(() => {
    if (!currentOrder || !currentOrder.items) {
      return 0;
    }
    
    // Count quantity of items that are ready for payment.
    return currentOrder.items.reduce((total: number, item: any) => {
      const isReadyForPayment = item.status && !['added_to_cart', 'paid'].includes(item.status);
      if (isReadyForPayment) {
        const qty = typeof item.quantity === 'number' ? item.quantity : 1;
        return total + qty;
      }
      return total;
    }, 0);
  }, [currentOrder]);

  const showReadyToPayFAB = readyToPayCount > 0;

  useEffect(() => {
    if (currentOrder && items.length > 0) {
      const cartItemsFromOrder = currentOrder.items
      .filter(orderItem => orderItem.status === 'added_to_cart')
      .map(orderItem => {
        const baseMenuItem = items.find(mi => mi.id === orderItem.menuItemId);
        if (!baseMenuItem) {
          console.warn(`Base menu item for order item ${orderItem.menuItemId} not found.`);
          return null;
        }

        const user = orderItem.user;
        if (!user) {
          console.warn(`Order item ${orderItem.id} is missing a user.`);
          return null;
        }
        
        const cartDisplayMenuItem: MenuItem = {
            ...baseMenuItem, 
            price: orderItem.price, // USE THE PRICE FROM THE OrderItem (includes customizations)
        };

        const cartItem: CartItem = {
          id: orderItem.id,
          menuItemId: orderItem.menuItemId,
          menuItem: cartDisplayMenuItem,
          name: orderItem.name,
          quantity: orderItem.quantity,
          price: orderItem.price,
          notes: orderItem.notes || undefined,
          selectedOptions: orderItem.options as Record<string, { name: string; price: number }[]> | undefined,
          status: orderItem.status || 'added_to_cart',
          user: user,
          orderItemUser: {
            user: user
          } 
        };
        return cartItem;
      }).filter((item): item is CartItem => item !== null);

      setCartItems(cartItemsFromOrder);
    } else {
      setCartItems([]);
    }
  }, [currentOrder, items]);

  // Set loading state immediately if we don't have required data
  useEffect(() => {
    if (!currentUser || !qrCode || userLoading) {
      dispatch(setPageLoading(true));
    }
  }, [currentUser, qrCode, userLoading, dispatch]);

  useEffect(() => {
    const fetchTableAndMenuData = async () => {
      if (!currentUser || !qrCode) {
        // Wait until user is initialized and we have a qrCode
        return;
      }

      dispatch(setPageLoading(true)); // Dispatch true at the beginning
      try {
        console.log('Fetching table and menu data...');
        
        // First, get the table info (this handles QR code scanning and session creation)
        // Parse query parameters to pass them to the backend
        const urlParams = new URLSearchParams(location.search);
        const fromQRCodeParam = urlParams.get('fromQRCode');
        
        const tableResponse = await api.get(`/tables/qr/${qrCode}`, {
          params: { fromQRCode: fromQRCodeParam }
        });
        console.log('Table response:', JSON.stringify(tableResponse.data, null, 2));
        
        // Then get menu data in parallel
        const [categoriesResponse, itemsResponse] = await Promise.all([
          api.get<Category[]>('/menu/categories'),
          api.get<MenuItem[]>('/menu/items')
        ]);
        
        console.log('Table response:', JSON.stringify(tableResponse.data, null, 2));
        setTable(tableResponse.data);

        // Extract and store restaurant ID
        const extractedRestaurantId = getRestaurantId(undefined, tableResponse.data);
        setRestaurantIdState(extractedRestaurantId);
        if (extractedRestaurantId) {
          setRestaurantId(extractedRestaurantId);
        }

        // Track table session and page view with enhanced context
        incrementTableVisit(tableResponse.data.id, extractedRestaurantId);
        trackWithUserContext(
          'table_accessed', 
          'table_session', 
          tableResponse.data.id, 
          undefined, 
          { qr_code: qrCode },
          currentUser,
          tableResponse.data.id,
          extractedRestaurantId
        );
        trackPageView(window.location.href, `Menu - Table ${tableResponse.data.id}`, extractedRestaurantId);

        // Check for fromQRCode query parameter and save tableId to localStorage
        const queryParams = new URLSearchParams(location.search);
        const fromQRCode = queryParams.get('fromQRCode');
        if (fromQRCode === 'true' && tableResponse.data.id) {
          console.log('Saving tableId to localStorage:', tableResponse.data.id);
          localStorage.setItem('tableId', tableResponse.data.id);
          trackWithUserContext(
            'qr_code_scanned', 
            'table_session', 
            tableResponse.data.id, 
            undefined, 
            { qr_code: qrCode, device_id: getDeviceId() },
            currentUser,
            tableResponse.data.id,
            extractedRestaurantId
          );
        }

        // Map backend data to frontend structure
        const mappedCategories = categoriesResponse.data.map(cat => ({
          id: cat.id,
          name: cat.name,
          description: cat.description,
          isActive: cat.isActive,
          displayOrder: cat.displayOrder
        }));

        const mappedItems = itemsResponse.data.map(item => ({
          id: item.id,
          name: item.name,
          description: item.description,
          price: Number(item.price),
          categoryId: String(item.categories?.[0]?.id || ''),
          imageUrl: item.imageUrl,
          isAvailable: item.isCurrentlyAvailable !== false, // Use the new availability field
          options: item.options,
          customization: item.customization,
          displayOrder: item.displayOrder,
          startTime: item.startTime,
          endTime: item.endTime,
          daysOfWeek: item.daysOfWeek,
          nextAvailabilityTime: item.nextAvailabilityTime
        }));
        
        setCategories(mappedCategories);
        setItems(mappedItems);

        // Track user identification
        if (currentUser) {
          identifyUser(currentUser.id, {
            user_name: currentUser.name,
            table_id: tableResponse.data.id,
          });
        }

        // Get or create current order for the table after items are loaded
        if (tableResponse.data.id) {
          console.log('Fetching current order for table:', tableResponse.data.id);
          const orderResult = await dispatch(getOrCreateCurrentOrder(tableResponse.data.id)).unwrap();
          console.log('Order loaded:', JSON.stringify(orderResult, null, 2));
          
          // Update restaurant ID from order data if available
          const orderRestaurantId = getRestaurantId(orderResult, tableResponse.data);
          if (orderRestaurantId && orderRestaurantId !== extractedRestaurantId) {
            setRestaurantIdState(orderRestaurantId);
            setRestaurantId(orderRestaurantId);
          }
          const finalRestaurantId = orderRestaurantId || extractedRestaurantId;

          // Fetch restaurant data if we have the restaurant ID
          if (finalRestaurantId) {
            try {
              const restaurantResponse = await api.get(`/restaurants/${finalRestaurantId}`);
              setRestaurantData(restaurantResponse.data);
            } catch (err) {
              console.error('Error fetching restaurant data:', err);
              // Don't fail the whole page if restaurant data fails
            }
          }

          // Track order event with enhanced context
          trackWithUserContext(
            'order_loaded', 
            'order', 
            orderResult.id, 
            orderResult.total,
            {
              items_count: orderResult.items?.length || 0,
              order_id: orderResult.id
            },
            currentUser,
            tableResponse.data.id,
            finalRestaurantId
          );

          // Track table-level metrics
          trackTableMetrics(tableResponse.data.id, orderResult, currentUser, finalRestaurantId);

          // NOT SURE ABOUT THIS ANYMORE, I DECIDED THAT IS EASIER TO JUST SHOW THE FAB FOR NOW
          // // Parse query parameters
          // const queryParams = new URLSearchParams(location.search);
          // const modifyOrder = queryParams.get('modifyOrder');

          // // EXISTING LOGIC: Check if the current user has items that are ready to pay (not in cart) and redirect if necessary (unless modifyOrder is true)
          // // Only redirect if the user has NO items in their cart
          // if (
          //   !modifyOrder &&
          //   orderResult &&
          //   orderResult.items &&
          //   currentUser
          // ) {
          //   const userItems = orderResult.items.filter((item: any) => item.user?.id === currentUser.id);
          //   const userHasItemsInCart = userItems.some((item: any) => item.status === 'added_to_cart');
          //   const userHasItemsReadyToPay = userItems.some(
          //     (item: any) => item.status && !['added_to_cart', 'paid'].includes(item.status)
          //   );
            
          //   // Only redirect if user has items ready to pay AND no items in cart
          //   if (userHasItemsReadyToPay && !userHasItemsInCart) {
          //     navigate(`/ready-to-pay/${orderResult.id}`);
          //     return; // Exit early to avoid running the second check
          //   }

          //   // NEW LOGIC: Also redirect if there are admin-added items (items added by non-standard users)
          //   const adminAddedItems = orderResult.items.filter((item: any) => {
          //     const userRole = item.user?.role;
          //     const isAdminAdded = userRole && ['owner', 'manager', 'staff'].includes(userRole);
          //     const isReadyForPayment = item.status && !['added_to_cart', 'paid'].includes(item.status);
          //     return isAdminAdded && isReadyForPayment;
          //   });
            
          //   if (adminAddedItems.length > 0) {
          //     navigate(`/ready-to-pay/${orderResult.id}`);
          //   }
          // }
        }
      } catch (err: any) {
        console.error('Error fetching data:', err);
        const errorMessage = err.response?.data?.message || 'No se pudieron cargar los datos de la mesa. Por favor, asegúrate de que el código QR es correcto e inténtalo de nuevo.';
        setError(errorMessage);
        navigate('/error');
      } finally {
        dispatch(setPageLoading(false)); // Dispatch false at the end
      }
    };

    fetchTableAndMenuData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCode, dispatch, currentUser, location.search]); // Add currentUser and location.search as dependencies

  // Connect to WebSocket when we have a current order
  useEffect(() => {
    if (currentOrder?.id) {
      wsService.connect(currentOrder.id);
    }
    return () => {
      wsService.disconnect();
    };
  }, [currentOrder?.id]);

  // Handle scroll position restoration when returning from customize page
  useEffect(() => {
    if (menuScrollState) {
      const { scrollY, selectedCategory: savedCategory, timestamp } = menuScrollState;
      
      // Only restore if the saved state is recent (within 5 minutes) to avoid stale states
      const isRecent = Date.now() - timestamp < 5 * 60 * 1000;
      
      if (isRecent) {
        // Restore category selection
        setSelectedCategory(savedCategory);
        
        // Restore scroll position after a short delay to ensure content is rendered
        const restoreScroll = () => {
          window.scrollTo({
            top: scrollY,
            behavior: 'auto' // Use 'auto' for immediate positioning
          });
          
          // Clear the saved state after restoration
          dispatch(clearMenuScrollState());
        };
        
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          // Add a small delay to ensure content is fully rendered
          setTimeout(restoreScroll, 100);
        });
      } else {
        // Clear stale state
        dispatch(clearMenuScrollState());
      }
    }
  }, [menuScrollState, dispatch]);

  // Handle FAB positioning based on scroll
  useEffect(() => {
    let debounceTimer: NodeJS.Timeout;

    const handleScroll = () => {
      // Debounce to handle Safari's rapid viewport changes
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const currentScrollY = window.scrollY;
        
        // Use current viewport height for real-time calculation
        const windowHeight = window.innerHeight;
        
        // Use document.body.scrollHeight for better iOS Safari support
        const documentHeight = Math.max(
          document.documentElement.scrollHeight,
          document.body.scrollHeight
        );
        
        // Generous threshold for mobile devices
        const threshold = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 120 : 80;
        
        // Simple bottom detection - if we're close to bottom, move FAB up
        const isAtBottom = currentScrollY + windowHeight >= documentHeight - threshold;
        
        setFabPosition(prev => ({
          isAtBottom: isAtBottom,
          lastScrollY: currentScrollY
        }));
      }, 50); // 50ms debounce to handle Safari viewport changes
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    window.addEventListener('orientationchange', handleScroll, { passive: true });
    
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      window.removeEventListener('orientationchange', handleScroll);
    };
  }, []);

  const handleCategoryChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedCategory(newValue);
    
    // Track category selection with enhanced context
    const selectedCategoryName = categories[newValue]?.name;
    if (selectedCategoryName) {
      trackWithUserContext(
        'category_selected',
        'menu_interaction',
        selectedCategoryName,
        undefined,
        {
          category_id: categories[newValue].id,
          category_name: selectedCategoryName,
          previous_category: categories[selectedCategory]?.name
        },
        currentUser,
        table?.id,
        restaurantId
      );
    }
  };

  const handleAddToCart = (item: MenuItem) => {
    // Track menu item click with enhanced context
    const categoryName = categories.find(cat => cat.id === item.categoryId)?.name;
    trackWithUserContext(
      'menu_item_clicked',
      'menu_interaction',
      item.name,
      item.price,
      {
        item_id: item.id,
        item_name: item.name,
        item_category: categoryName,
        item_price: item.price,
        item_available: item.isAvailable
      },
      currentUser,
      table?.id,
      restaurantId
    );
    
    // Save current scroll position and category before navigating
    dispatch(setMenuScrollState({
      scrollY: window.scrollY,
      selectedCategory,
      timestamp: Date.now()
    }));
    
    navigate(`/customize-item/${item.id}`);
  };

  const handleUpdateQuantity = async (itemId: string, newQuantity: number) => {
    if (!currentOrder) return;
    
    const orderItem = currentOrder.items.find(item => item.id === itemId);
    if (!orderItem) return;

    const groupKey = getGroupKeyForCharacteristics(orderItem.menuItemId, orderItem.notes, orderItem.options);
    setItemActionLoading(prev => ({ ...prev, [groupKey]: true }));

    try {
      await dispatch(updateOrderItemAsync({
          orderId: currentOrder.id,
        itemId,
        updates: { 
          quantity: newQuantity,
          price: orderItem.price,
          notes: orderItem.notes,
          options: orderItem.options
          }
        })).unwrap();
      
      // Track quantity update with enhanced context
      trackWithUserContext(
        'quantity_updated',
        'cart',
        orderItem.name,
        orderItem.price * newQuantity,
        {
          item_id: orderItem.menuItemId,
          item_name: orderItem.name,
          old_quantity: orderItem.quantity,
          new_quantity: newQuantity,
          price_per_item: orderItem.price,
          total_price: orderItem.price * newQuantity
        },
        currentUser,
        table?.id,
        restaurantId
      );
      } catch (err: any) {
      console.error('Error updating quantity:', err);
      if (err.response?.status >= 500) {
        navigate('/error');
        return;
      }
      setError(err.message || 'No se pudo actualizar la cantidad');
    } finally {
      setItemActionLoading(prev => ({ ...prev, [groupKey]: false }));
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!currentOrder) return;
    
    const orderItem = currentOrder.items.find(item => item.id === itemId);
    if (!orderItem) return;

    const groupKey = getGroupKeyForCharacteristics(orderItem.menuItemId, orderItem.notes, orderItem.options);
    setItemActionLoading(prev => ({ ...prev, [groupKey]: true }));

    try {
      await dispatch(removeOrderItemAsync({
        orderId: currentOrder.id,
        itemId
      })).unwrap();
      
      // Track item removal with enhanced context
      const baseMenuItem = items.find(mi => mi.id === orderItem.menuItemId);
      const categoryName = baseMenuItem ? categories.find(cat => cat.id === baseMenuItem.categoryId)?.name : undefined;
      
      trackWithUserContext(
        'item_removed',
        'cart',
        orderItem.name,
        orderItem.price * orderItem.quantity,
        {
          item_id: orderItem.menuItemId,
          item_name: orderItem.name,
          item_category: categoryName,
          quantity: orderItem.quantity,
          price_per_item: orderItem.price,
          total_removed_value: orderItem.price * orderItem.quantity
        },
        currentUser,
        table?.id,
        restaurantId
      );
      
      // Also track e-commerce remove from cart event
      trackRemoveFromCart('CRC', orderItem.price * orderItem.quantity, [{
        item_id: orderItem.menuItemId,
        item_name: orderItem.name,
        item_category: categoryName,
        quantity: orderItem.quantity,
        price: orderItem.price,
      }]);
    } catch (err: any) {
      console.error('Error removing item:', err);
      if (err.response?.status >= 500) {
        navigate('/error');
        return;
      }
      setError(err.message || 'No se pudo eliminar el artículo');
    } finally {
      setItemActionLoading(prev => ({ ...prev, [groupKey]: false }));
    }
  };

  const handleConfirmAddToCart = async () => {
    if (!selectedItem || !currentOrder) return;

    setIsAddingToCart(true);
    try {
      const formattedOptions: SelectedOptions = Object.entries(selectedOptions).reduce((acc, [groupName, choices]) => {
        acc[groupName] = choices.map(choice => ({
          name: choice.name
        }));
        return acc;
      }, {} as SelectedOptions);

      const orderItemData = {
        menuItemId: selectedItem.id,
        name: selectedItem.name,
        quantity: quantity,
        notes: specialInstructions,
        selectedOptions: formattedOptions,
      };

      await dispatch(addOrderItemAsync({ orderId: currentOrder.id, item: orderItemData })).unwrap();
      setSelectedOptions({});
      setSpecialInstructions('');
      setQuantity(1);
      setSelectedItem(null);
      setOpenDialog(false);
    } catch (err: any) {
      console.error('Error adding item to cart:', err);
      if (err.response?.status >= 500) {
        navigate('/error');
        return;
      }
      setError(err.message || 'No se pudo agregar el artículo al carrito');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleRemoveFromCart = async (
    groupCharacteristics: { menuItemId: string; notes?: string; selectedOptions?: CartItem['selectedOptions'] }
  ) => {
    if (!currentOrder) return;

    const { menuItemId, notes, selectedOptions } = groupCharacteristics;
    const groupKey = getGroupKeyForCharacteristics(menuItemId, notes, selectedOptions);

    setItemActionLoading(prev => ({ ...prev, [groupKey]: true }));

    try {
      const itemsToRemove = currentOrder.items.filter(orderItem => {
        // We need to find the original menuItemId for each orderItem
        // This assumes orderItem.menuItemId exists and is correct.
        const orderItemMenuItemId = orderItem.menuItemId; 
        
        return (
          orderItemMenuItemId === menuItemId &&
          (notes || undefined) === (orderItem.notes || undefined) && // Normalize undefined/empty strings for notes
          optionsAreEqual(selectedOptions, orderItem.options as CartItem['selectedOptions'])
        );
      });

      if (itemsToRemove.length === 0) {
        console.warn("No items found in order to remove for group:", groupCharacteristics);
        setItemActionLoading(prev => ({ ...prev, [groupKey]: false }));
        return;
      }

      await Promise.all(
        itemsToRemove.map(orderItem =>
          dispatch(removeOrderItemAsync({
            orderId: currentOrder.id,
            itemId: orderItem.id, // removeOrderItemAsync expects the specific OrderItem ID
            // notes and selectedOptions are not strictly needed by removeOrderItemAsync if it only uses itemId
          })).unwrap()
        )
      );
    } catch (err: any) {
      console.error('Error removing item group:', err);
      if (err.response?.status >= 500) {
        navigate('/error');
        return;
      }
      setError(err.response?.data?.message || 'No se pudo eliminar el grupo de artículos');
    } finally {
      setItemActionLoading(prev => ({ ...prev, [groupKey]: false }));
    }
  };

  const handleCheckout = () => {
    // The cartItems state is already filtered to only include items with 'added_to_cart' status.
    // We just need to check if any of them belong to the current user.
    const userHasItemsToCheckout = cartItems.some(
      item => item.user?.id === currentUser?.id
    );

    if (userHasItemsToCheckout) {
      // Track begin checkout
      const userItems = cartItems.filter(item => item.user?.id === currentUser?.id);
      const checkoutValue = userItems.reduce((sum, item) => sum + ((item.price || 0) * item.quantity), 0);
      const checkoutItems = userItems.map(item => ({
        item_id: item.menuItemId,
        item_name: item.name,
        item_category: categories.find(cat => cat.id === items.find(mi => mi.id === item.menuItemId)?.categoryId)?.name,
        quantity: item.quantity,
        price: item.price || 0,
      }));
      
      // Track enhanced checkout event
      trackWithUserContext(
        'checkout_initiated',
        'conversion',
        currentOrder?.id || 'unknown',
        checkoutValue,
        {
          items_count: userItems.length,
          has_social_payment_opportunity: cartItems.some(item => item.user?.id !== currentUser?.id),
          checkout_items: checkoutItems
        },
        currentUser,
        table?.id,
        restaurantId
      );
      
      // Also track e-commerce begin checkout event
      trackBeginCheckout('CRC', checkoutValue, checkoutItems);
      
      // If the user has items to check out, show the confirmation dialog.
      setIsConfirming(true);
    } else {
      // Track navigation to ready to pay with enhanced context
      trackWithUserContext(
        'navigate_to_payment',
        'engagement',
        'no_items_to_checkout',
        undefined,
        { 
          has_items_to_checkout: false,
          ready_items_count: readyToPayCount
        },
        currentUser,
        table?.id,
        restaurantId
      );
      
      // Otherwise, navigate directly to the payment page.
      if (currentOrder) {
        navigate(`/ready-to-pay/${currentOrder.id}`);
      }
    }
  };

  const handleConfirmCheckout = async () => {
    if (!currentOrder) return;
    setIsConfirming(false);
    dispatch(setPageLoading(true));
    dispatch(setCartOpen(false)); // Close the cart drawer
    
    // Track checkout confirmation with enhanced context
    trackWithUserContext(
      'checkout_confirmed',
      'engagement',
      currentOrder?.id || 'unknown',
      undefined,
      { 
        order_id: currentOrder?.id,
        user_confirmed_checkout: true
      },
      currentUser,
      table?.id,
      restaurantId
    );
    
    try {
      await dispatch(prepareMyItemsAsync(currentOrder.id)).unwrap();
      
      // Track successful item preparation with enhanced context
      trackWithUserContext(
        'items_prepared_for_payment',
        'order',
        currentOrder?.id || 'unknown',
        undefined,
        { 
          order_id: currentOrder?.id,
          preparation_successful: true
        },
        currentUser,
        table?.id,
        restaurantId
      );
      
      navigate(`/ready-to-pay/${currentOrder.id}`);
    } catch (err: any) {
      console.error('Error preparing items for payment:', err);
      if (err.response?.status >= 500) {
        navigate('/error');
        return;
      }
      setError(err.message || 'No se pudieron preparar los artículos para el pago');
    } finally {
      dispatch(setPageLoading(false));
    }
  };

  // Calculate cart total based on items with 'added_to_cart' status
  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);
  
  // Only count items belonging to the current user for the FAB
  const cartCount = cartItems
    .filter(item => item.user?.id === currentUser?.id)
    .reduce((sum, item) => sum + item.quantity, 0);
  


  if (error) {
    return null;
  }

  // Show nothing while loading - GlobalLoader will be displayed from App.tsx
  // Check for all loading conditions that should prevent content from showing
  if (isPageLoading || userLoading || !currentUser || !qrCode || items.length === 0) {
    return null;
  }

  return (
    <Box sx={{
      minHeight: ['100vh', '100dvh', '-webkit-fill-available'], // iOS Safari viewport handling
      width: '100vw',
      background: '#f6f6f8',
      paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      paddingLeft: 'env(safe-area-inset-left, 0px)',
      paddingRight: 'env(safe-area-inset-right, 0px)',
      fontFamily: 'Inter, system-ui, sans-serif' 
    }}>
      <Container maxWidth={false} disableGutters sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 1, maxWidth: '100%', mx: 'auto', width: '100%' }}>
        {/* Header Image */}
        {restaurantData?.imageUrl && (
          <Box sx={{ width: '100%', height: 190, borderRadius: '8px', overflow: 'hidden', mb: 1.5, boxShadow: '0 1px 4px rgba(60,72,100,0.06)' }}>
            <img
              src={restaurantData.imageUrl}
              alt={`${restaurantData.name} - Restaurant`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '8px' }}
            />
          </Box>
        )}

        {/* Categories Section */}
        <Box sx={{ 
          position: 'sticky', 
          top: 0, 
          zIndex: 1000, 
          background: '#f6f6f8', // Simplified background to prevent compositing issues
          pt: 1.5,
          pb: 1,
          width: '100%',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          // Removed backdropFilter which can cause layout performance issues
        }}>          

          <Box sx={{ width: '100%', px: 0.5, mb: 1, overflowX: 'auto' }}>
            <Tabs
              value={selectedCategory}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{ style: { display: 'none' } }}
              sx={{
                minHeight: 0,
                '& .MuiTabs-flexContainer': { 
                  gap: 1.2, // Slightly reduced gap to show more pills
                  justifyContent: categories.length <= 3 ? 'center' : 'flex-start', // Center when few categories
                  px: 0.5, // Reduced padding to show more content
                },
                '& .MuiTabs-scrollButtons': {
                  color: '#4f8cff', // Match app theme
                  width: 32, // Smaller scroll buttons
                  minWidth: 32,
                  '&.Mui-disabled': {
                    opacity: 0.2,
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.2rem', // Smaller arrow icons
                  },
                },
                '& .MuiTabs-scroller': {
                  '& .MuiTabs-flexContainer': {
                    paddingLeft: '4px', // Minimal left padding
                    paddingRight: '4px', // Minimal right padding
                  },
                },
                '& .MuiTab-root': {
                  borderRadius: '12px',
                  minHeight: 44, // Increased height for better touch interaction
                  minWidth: 0,
                  px: 3, // Increased horizontal padding
                  py: 1.2, // Increased vertical padding for better touch target
                  fontWeight: 600,
                  fontSize: '0.97rem',
                  textTransform: 'none',
                  background: '#fff',
                  border: '1.5px solid #e8e9eb', // Slightly thicker border
                  color: '#374151', // Improved contrast
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.04)', // Softer shadow
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', // Smoother transition
                  mb: 0.5,
                  cursor: 'pointer',
                  '&.Mui-selected': {
                    background: '#4f8cff', // App's primary blue color
                    color: '#fff',
                    border: '1.5px solid #4f8cff',
                    boxShadow: '0 4px 12px 0 rgba(79, 140, 255, 0.3)', // Enhanced shadow for selected state
                    // Removed transform to prevent layout shifts
                  },
                  '&:hover:not(.Mui-selected)': {
                    boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.08)',
                    background: '#f8fafc',
                    borderColor: '#d1d5db',
                    // Removed transform to prevent layout shifts
                  },
                  '&.Mui-focusVisible': {
                    outline: '2px solid #667eea',
                    outlineOffset: '2px',
                    boxShadow: 'none',
                  },
                  '&:focus': {
                    outline: 'none',
                  },
                  // Better spacing between pills
                  marginRight: '8px',
                },
              }}
            >
              {categories.map((category, index) => (
                <Tab key={category.id} label={category.name} id={`menu-tab-${index}`} />
              ))}
            </Tabs>
          </Box>

        </Box>

        {/* Category Description Section */}
        <Box sx={{ 
          width: '100%', 
          mb: 1, // Reduced margin
          px: 1, 
          py: 0.5, // Reduced padding
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: '8px', // Smaller border radius
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
        }}>
          <Typography variant="h6" sx={{ // Changed from h5 to h6
            fontWeight: 700, 
            mb: 0, // Reduced margin
            color: '#1e293b', 
            fontSize: '1.1rem', // Smaller font size
            letterSpacing: '-0.3px',
            textAlign: 'center',
          }}>
            {categories[selectedCategory]?.name}
          </Typography>
          
          <Typography variant="body2" sx={{ // Changed from body1 to body2
            fontWeight: 400, 
            color: '#475569', 
            fontSize: '0.85rem', // Smaller font size
            lineHeight: 1.4, // Tighter line height
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2, // Reduced from 3 to 2 lines
            WebkitBoxOrient: 'vertical',
            minHeight: '1.4em', // Adjusted for smaller text
            maxHeight: '2.8em', // Maximum 2 lines
            fontStyle: 'normal',
            opacity: 1,
          }}>
            {categories[selectedCategory]?.description || 'Explora nuestros deliciosos artículos del menú'}
          </Typography>
        </Box>

        {/* Divider */}
        <Box sx={{ width: '100%', height: 1, background: '#ececec', mb: 1.5 }} />

        {categories.map((category, index) => {
          const filteredItems = items.filter((item) => String(item.categoryId) === String(category.id));
          // Sort items: available items first, then unavailable items
          const sortedItems = [...filteredItems].sort((a, b) => {
            if (a.isAvailable && !b.isAvailable) return -1;
            if (!a.isAvailable && b.isAvailable) return 1;
            return 0;
          });
          return (
            <TabPanel key={category.id} value={selectedCategory} index={index}>              
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)', // More explicit and consistent
                gap: '8px',
                px: 0,
                pb: 0,
                pt: 0,
                m: 0,
                width: '100%', // Ensure full width
                minHeight: 0, // Prevent layout shifts
                position: 'relative', // Establish positioning context
              }}>
                {sortedItems.map((item) => (
                  <Card key={item.id} sx={{
                    background: '#fff',
                    border: '1px solid #ececec',
                    boxShadow: '0 1px 4px rgba(60,72,100,0.06)',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    p: 0,
                    mb: 1,
                    height: '100%', // Ensure cards take full height of their grid cell
                    cursor: item.isAvailable ? 'pointer' : 'default', // Change cursor based on availability
                  }}
                  onClick={() => item.isAvailable && handleAddToCart(item)} // Only call handleAddToCart if item is available
                  >
                    {/* Remove onClick from the Button and let the Card handle it */}
                    {item.imageUrl && (
                      <Box sx={{
                        width: '100%',
                        position: 'relative',
                        paddingTop: '75%', // 4:3 aspect ratio
                        overflow: 'hidden',
                        borderTopLeftRadius: '6px',
                        borderTopRightRadius: '6px',
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      }}>
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            borderTopLeftRadius: 6,
                            borderTopRightRadius: 6,
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                          }}
                        />
                      </Box>
                    )}
                    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                      <Typography variant="h6" component="div" sx={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#181818',                        
                        textAlign: 'left',
                        lineHeight: 1.2,
                        maxHeight: '2.4em',
                        minHeight: '2.4em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                       }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', mb: 0.5, textAlign: 'left', lineHeight: 1.2 }}>
                         ₡{Number(item.price).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </Typography>
                      <Typography sx={{
                        fontWeight: 400,
                        color: '#888',
                        fontSize: '0.95rem',
                        mb: 1,
                        textAlign: 'left',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2, // Show 2 lines before ellipsis
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.3,
                        minHeight: '3.9em', // Approximately 3 lines (1.3 * 3)
                        height: '3.9em', // Fixed height for description area
                      }}>
                        {item.description}
                      </Typography>
                      {!item.isAvailable && item.nextAvailabilityTime && (
                        <Typography sx={{
                          fontWeight: 500,
                          color: '#666',
                          fontSize: '0.8rem',
                          mb: 0.5,
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}>
                          {item.nextAvailabilityTime}
                        </Typography>
                      )}
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          borderRadius: '6px',
                          fontWeight: 600,
                          py: 0.5, // Reduced padding
                          fontSize: '0.85rem', // Reduced font size
                          mt: 1,
                          whiteSpace: 'nowrap', // Ensure text is on one line
                          ...(item.isAvailable ? {} : {
                            background: '#f5f5f5',
                            color: '#999',
                            '&:hover': {
                              background: '#f5f5f5',
                              color: '#999'
                            }
                          })
                        }}
                        disabled={!item.isAvailable}
                        // onClick={() => handleAddToCart(item)} // Removed onClick from Button
                      >
                        {item.isAvailable ? 'Agregar al Carrito' : 'No Disponible'}
                      </Button>
                    </Box>
                  </Card>
                ))}
              </Box>
            </TabPanel>
          );
        })}

        {/* Floating Action Button */}
        {!cartOpen &&
          (cartCount > 0 ? (
            <Fab
              data-testid="cart-fab"
              aria-label="carrito"
              onClick={() => {
                dispatch(setCartOpen(true));
                trackWithUserContext(
                  'cart_opened',
                  'cart',
                  'fab_clicked',
                  cartTotal,
                  {
                    items_count: cartCount,
                    total_value: cartTotal
                  },
                  currentUser,
                  table?.id,
                  restaurantId
                );
              }}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1200,
                minWidth: 140,
                height: 56,
                borderRadius: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2.5,
                minHeight: 'unset',
                overflow: 'visible',
                background: '#f3f5fa',
                border: '1.5px solid #e0e0e0',
                color: '#222',
                boxShadow: 'none',
                transform: fabPosition.isAtBottom ? 'translateY(-40px)' : 'translateY(0)',
                transition: 'box-shadow 0.18s, border 0.18s, transform 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: '0 2px 8px 0 rgba(60,72,100,0.10)',
                  background: '#f5f7ff',
                  border: '1.5px solid #4f8cff',
                },
                '&:focus': {
                  boxShadow: '0 0 0 2px #4f8cff',
                  border: '1.5px solid #4f8cff',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Badge badgeContent={cartCount} overlap="circular" sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem', minWidth: 18, height: 18, background: '#d0dbf7', color: '#222', fontWeight: 600, border: '1.5px solid #fff', boxShadow: '0 2px 8px 0 rgba(60,72,100,0.10)' } }}>
                  <ShoppingCartIcon sx={{ fontSize: 28, color: '#222' }} />
                </Badge>
                <span style={{ fontWeight: 600, fontSize: '1.08rem', color: '#222', whiteSpace: 'nowrap' }}>
                  ₡{cartTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </Box>
            </Fab>
          ) : showReadyToPayFAB && currentOrder ? (
            <Fab
              aria-label="ir a pagar"
              onClick={() => {
                trackWithUserContext(
                  'ready_to_pay_clicked',
                  'engagement',
                  'fab_clicked',
                  undefined,
                  { 
                    order_id: currentOrder?.id, 
                    ready_items_count: readyToPayCount 
                  },
                  currentUser,
                  table?.id,
                  restaurantId
                );
                navigate(`/ready-to-pay/${currentOrder?.id}`);
              }}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1200,
                minWidth: 140,
                height: 56,
                borderRadius: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',                
                minHeight: 'unset',
                overflow: 'visible',
                background: '#f3f5fa',
                border: '1.5px solid #e0e0e0',
                color: '#222',
                boxShadow: 'none',
                textTransform: 'none',
                transform: fabPosition.isAtBottom ? 'translateY(-40px)' : 'translateY(0)',
                transition: 'box-shadow 0.18s, border 0.18s, transform 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: '0 2px 8px 0 rgba(60,72,100,0.10)',
                  background: '#f5f7ff',
                  border: '1.5px solid #4f8cff',
                },
                '&:focus': {
                  boxShadow: '0 0 0 2px #4f8cff',
                  border: '1.5px solid #4f8cff',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Badge badgeContent={readyToPayCount} overlap="circular" sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem', minWidth: 18, height: 18, background: '#d0dbf7', color: '#222', fontWeight: 600, border: '1.5px solid #fff', boxShadow: '0 2px 8px 0 rgba(60,72,100,0.10)' } }}>
                  <PaymentIcon sx={{ fontSize: 28, color: '#222' }} />
                </Badge>
                <span style={{ fontWeight: 600, fontSize: '1.08rem', color: '#222', whiteSpace: 'nowrap' }}>
                  Pagar
                </span>
              </Box>
            </Fab>
          ) : null)}

        {/* Cart Drawer */}
        <CartDrawer
          open={cartOpen}
          onClose={() => {
            dispatch(setCartOpen(false));
            trackWithUserContext(
              'cart_closed',
              'cart',
              'drawer_closed',
              cartTotal,
              {
                items_count: cartCount,
                total_value: cartTotal
              },
              currentUser,
              table?.id,
              restaurantId
            );
          }}
          cart={cartItems}
          total={cartTotal}
          onRemoveItem={handleRemoveItem}
          onRemoveGroup={handleRemoveFromCart}
          onUpdateQuantity={handleUpdateQuantity}
          onCheckout={handleCheckout}
          itemActionLoading={itemActionLoading}
        />

        <Dialog open={isConfirming} onClose={() => setIsConfirming(false)}>
          <DialogTitle>Confirmar Envío</DialogTitle>
          <DialogContent>
            <Typography>
              Estás a punto de enviar los productos que <b>tú</b> ordenaste a la cocina. ¿Estás de acuerdo o deseas continuar comprando?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsConfirming(false)}>Continuar Comprando</Button>
            <Button onClick={handleConfirmCheckout} color="primary" variant="contained">
              Sí, Enviar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default PublicMenu; 